import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Toast } from 'primereact/toast';
import Spinner from '../ogComponent/spinner';
import { IconArrowRight, IconPlus, IconClock, IconCalendar } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TextField, Pagination } from '@mui/material';
import dayjs from 'dayjs';
import "./std_regular.css";

export default function StdRegular() {
    const navigate = useNavigate();
    const toast = useRef(null);
    const [showModal, setShowModal] = useState(false);
    const [product, setProduct] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 10;

    const [formData, setFormData] = useState({
        punch_in_date: '',
        punch_in_time: null,
        punch_out_date: '',
        punch_out_time: null,
        reason: '',
    });

    const permissions = useSelector((state) => state.emp.permissions);

    // Pagination logic
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = product.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(product.length / rowsPerPage);

    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage);
    };

    useEffect(() => {
        if (permissions.includes("showmyRegularizationrequestID")) {
            fetchAttendanceData();
        } else {
            setLoading(false);
        }
    }, [permissions]);

    const fetchAttendanceData = async () => {
        setLoading(true);
        const userId = localStorage.getItem('userId');
        const accessToken = localStorage.getItem('accessToken');

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API}/api/attendance/attendance-regularization/?employee_id=${userId}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            if (!response.ok) throw new Error('Network response was not ok');

            const result = await response.json();
            const sortedData = (result.data || []).sort((a, b) => new Date(b.date || b.punch_in_date) - new Date(a.date || a.punch_in_date));
            setProduct(sortedData);
        } catch (error) {
            console.error('Error fetching punch request data:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch punch requests',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setShowModal(false);
        setFormData({
            punch_in_date: '',
            punch_in_time: null,
            punch_out_date: '',
            punch_out_time: null,
            reason: '',
        });
    };

    const handleShow = () => {
        if (permissions.includes("requestRegularizationID")) {
            setShowModal(true);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({ ...prevState, [name]: value }));
    };

    const handleTimeChange = (field, value) => {
        setFormData(prevState => ({ ...prevState, [field]: value }));
    };

    const validateForm = () => {
        const hasPunchInDate = formData.punch_in_date.trim() !== '';
        const hasPunchInTime = formData.punch_in_time !== null;
        const hasPunchOutDate = formData.punch_out_date.trim() !== '';
        const hasPunchOutTime = formData.punch_out_time !== null;

        const hasCompletePunchIn = hasPunchInDate && hasPunchInTime;
        const hasCompletePunchOut = hasPunchOutDate && hasPunchOutTime;

        if (!hasCompletePunchIn && !hasCompletePunchOut) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Provide at least one complete Punch In or Punch Out set',
                life: 3000
            });
            return false;
        }

        if ((hasPunchInDate && !hasPunchInTime) || (!hasPunchInDate && hasPunchInTime) ||
            (hasPunchOutDate && !hasPunchOutTime) || (!hasPunchOutDate && hasPunchOutTime)) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Provide both Date and Time for a complete request',
                life: 3000
            });
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!permissions.includes("requestRegularizationID")) return;
        if (!validateForm()) return;

        const accessToken = localStorage.getItem('accessToken');
        const payload = { reason: formData.reason };

        if (formData.punch_in_date && formData.punch_in_time) {
            payload.punch_in_date = formData.punch_in_date;
            payload.punch_in_time = formData.punch_in_time.format('hh:mm A');
        }

        if (formData.punch_out_date && formData.punch_out_time) {
            payload.punch_out_date = formData.punch_out_date;
            payload.punch_out_time = formData.punch_out_time.format('hh:mm A');
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API}/api/attendance/attendance-regularization-request/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error('Response not ok');

            const result = await response.json();
            if (result.success) {
                toast.current.show({ severity: 'success', summary: 'Success', detail: 'Sent successfully', life: 3000 });
                handleClose();
                fetchAttendanceData();
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: result.message || 'Failed to send', life: 3000 });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to send Request', life: 3000 });
        }
    };

    const formatTime = (time) => {
        if (!time) return null;
        if (typeof time !== 'string') return time;
        if (time.includes('AM') || time.includes('PM')) return time;
        return dayjs(`2000-01-01 ${time}`).format('hh:mm A');
    };

    if (loading) return <Spinner />;

    return (
        <div className='punch-request-container w-100 d-flex flex-column p-0 gap-4'>
            <Toast ref={toast} position="bottom-right" />

            <div className="request-header px-4 pt-4">
                <div className="d-flex flex-column">
                    <h3 className="m-0">My Punch Requests</h3>
                    <p className="text-muted small mb-0 mt-1">Track and manage your attendance regularization requests.</p>
                </div>
                <div className="d-flex gap-3 align-items-center">
                    {permissions.includes("showallRegularizationrequestID") && (
                        <button className='view-all-btn' onClick={() => navigate('/user/punchreq')}>
                            All Requests <IconArrowRight size={18} />
                        </button>
                    )}
                    {permissions.includes("requestRegularizationID") && (
                        <button className='request-btn-premium' onClick={handleShow}>
                            <IconPlus size={20} /> New Request
                        </button>
                    )}
                </div>
            </div>

            <div className="px-4 pb-5">
                {permissions.includes("showmyRegularizationrequestID") ? (
                    product.length === 0 ? (
                        <div className="request-card d-flex flex-column justify-content-center align-items-center py-5">
                            <img src="/assets/file.png" alt="empty" style={{ width: 120, opacity: 0.2 }} />
                            <p className="mt-3 fw-medium text-muted">No regularization requests found</p>
                        </div>
                    ) : (
                        <div className="request-card">
                            <div className="table-responsive">
                                <table className="punch-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Punch In</th>
                                            <th>Punch Out</th>
                                            <th>Reason</th>
                                            <th className="text-center">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentRows.map((item, index) => (
                                            <tr key={index}>
                                                <td data-label="#">{indexOfFirstRow + index + 1}</td>
                                                <td data-label="Punch In">
                                                    {item.punch_in_date ? (
                                                        <div className="d-flex flex-column gap-1">
                                                            <span className="date-pill w-fit"><IconCalendar size={14} className="me-1" /> {dayjs(item.punch_in_date).format('DD MMM')}</span>
                                                            <span className="time-pill w-fit"><IconClock size={14} className="me-1" /> {formatTime(item.punch_in_time)}</span>
                                                        </div>
                                                    ) : <span className="text-muted">—</span>}
                                                </td>
                                                <td data-label="Punch Out">
                                                    {item.punch_out_date ? (
                                                        <div className="d-flex flex-column gap-1">
                                                            <span className="date-pill w-fit"><IconCalendar size={14} className="me-1" /> {dayjs(item.punch_out_date).format('DD MMM')}</span>
                                                            <span className="time-pill w-fit"><IconClock size={14} className="me-1" /> {formatTime(item.punch_out_time)}</span>
                                                        </div>
                                                    ) : <span className="text-muted">—</span>}
                                                </td>
                                                <td data-label="Reason">
                                                    <span className="text-muted truncate" style={{ maxWidth: '200px', display: 'block' }}>{item.reason}</span>
                                                </td>
                                                <td data-label="Status" className="text-center">
                                                    <div className={`status-indicator ${dayjs(item.status).isValid() || item.status === 'Approved' ? 'status-approved' :
                                                        item.status === 'Rejected' ? 'status-rejected' : 'status-pending'
                                                        }`}>
                                                        <span className="status-dot"></span>
                                                        {item.status || "Pending"}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {totalPages > 1 && (
                                <div className="d-flex justify-content-center mt-4">
                                    <Pagination
                                        count={totalPages}
                                        page={currentPage}
                                        onChange={handlePageChange}
                                        color="primary"
                                        size="medium"
                                        sx={{ "& .MuiPaginationItem-root": { fontWeight: 600, borderRadius: '10px' } }}
                                    />
                                </div>
                            )}
                        </div>
                    )
                ) : (
                    <div className="alert alert-warning">No permission to view punch requests.</div>
                )}
            </div>

            {/* Request Modal */}
            <div className={`modal fade ${showModal ? 'show' : ''}`} style={{ display: showModal ? 'block' : 'none' }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content modern-modal-content">
                        <div className="modern-modal-header d-flex justify-content-between align-items-center">
                            <h5 className="m-0 fw-bold">Request Attendance Correction</h5>
                            <button type="button" className="btn-close shadow-none" onClick={handleClose}></button>
                        </div>
                        <div className="modern-modal-body">
                            <form onSubmit={handleSubmit}>
                                <div className="row g-4">
                                    <div className="col-12"><small className="text-muted fw-bold text-uppercase">Punch In Details</small></div>
                                    <div className="col-6">
                                        <label className="input-label-modern">Date</label>
                                        <input type="date" className="form-control modern-input" name="punch_in_date" value={formData.punch_in_date} onChange={handleInputChange} />
                                    </div>
                                    <div className="col-6">
                                        <label className="input-label-modern">Time</label>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <TimePicker
                                                value={formData.punch_in_time}
                                                onChange={(v) => handleTimeChange("punch_in_time", v)}
                                                ampm={true}
                                                format="hh:mm A"
                                                renderInput={(p) => <TextField {...p} fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: '12px' } }} />}
                                            />
                                        </LocalizationProvider>
                                    </div>

                                    <div className="col-12 mt-4"><small className="text-muted fw-bold text-uppercase">Punch Out Details</small></div>
                                    <div className="col-6">
                                        <label className="input-label-modern">Date</label>
                                        <input type="date" className="form-control modern-input" name="punch_out_date" value={formData.punch_out_date} onChange={handleInputChange} />
                                    </div>
                                    <div className="col-6">
                                        <label className="input-label-modern">Time</label>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <TimePicker
                                                value={formData.punch_out_time}
                                                onChange={(v) => handleTimeChange("punch_out_time", v)}
                                                ampm={true}
                                                format="hh:mm A"
                                                renderInput={(p) => <TextField {...p} fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: '12px' } }} />}
                                            />
                                        </LocalizationProvider>
                                    </div>

                                    <div className="col-12 mt-4">
                                        <label className="input-label-modern">Reason for Request</label>
                                        <textarea className="form-control modern-input" name="reason" rows="3" placeholder="Explain why you are requesting this correction..." value={formData.reason} onChange={handleInputChange}></textarea>
                                    </div>
                                </div>
                                <button type="submit" className="modal-submit-btn mt-4">Submit Regularization Request</button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            {showModal && <div className="modal-backdrop fade show" onClick={handleClose}></div>}
        </div>
    );
}
